/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useSession } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/providers/ToastProvider";
import api from "../services/api";
import { usePlans } from "../context/PlanContext";

// Management Component Imports
import TopicManager from "../components/dashboard/TopicManager";
import CategoryManager from "../components/dashboard/CategoryManager";
import MembershipLimitManager from "../components/dashboard/MembershipLimitManager";
import TopicCounter from "../components/dashboard/TopicCounter";
import CategoryCounter from "../components/dashboard/CategoryCounter";

export default function Settings() {
  const { data: session, status } = useSession();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getPlanByName } = usePlans();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Management navigation states
  const [showManager, setShowManager] = useState<"topics" | "categories" | "limits" | null>(null);

  const user = session?.user;
  const role = user?.role || "STUDENT";
  const membership = user?.highest_plan_name || user?.membership || "FREE";
  const isManager = role === "ADMIN" || role === "COACH" || role === "COACHING_ASSOCIATE";
  const isAdmin = role === "ADMIN";

  const getTierDetails = (tier: string) => {
    const plan = getPlanByName(tier);
    if (plan && plan.icon_color) {
      return { color: `bg-[${plan.icon_color}]/10 text-[${plan.icon_color}] border-[${plan.icon_color}]/20`, icon: "military_tech" };
    }
    // Fallback if not found or no custom color
    switch (tier.toUpperCase()) {
      case "VIP": return { color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20", icon: "auto_awesome" };
      case "PREMIUM": return { color: "bg-amber-500/10 text-amber-500 border-amber-500/20", icon: "workspace_premium" };
      case "GROWTH": return { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: "military_tech" };
      default: return { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", icon: "person" };
    }
  };
  const tierDetails = getTierDetails(membership);

  useEffect(() => {
    if (status === "unauthenticated") {
      navigate("/login");
    }
  }, [status, navigate]);

  const handleSSOHandoff = async () => {
    setIsSubmitting(true);
    try {
      const res = await api.post("/auth/portal");
      if (res.data?.magic_link) {
        window.location.href = res.data.magic_link;
      } else {
        showToast("Handoff Failed", "Could not establish a secure connection to the Auth Hub.", "error");
      }
    } catch (err: any) {
      console.error("SSO Handoff failed", err);
      showToast("Handoff Failed", err.response?.data?.error || "Could not reach the security server.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col gap-2 px-2">
        <h2 className="text-4xl font-black text-on-surface tracking-tighter leading-tight">System Settings</h2>
        <p className="text-on-surface-variant font-medium">Manage your curriculum and execution environment.</p>
      </div>

      {!showManager ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-2">
          {/* User Profile Summary Card */}
          <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] flex flex-col justify-between space-y-6 shadow-sm hover:shadow-md transition-all">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-surface-container-highest border border-outline-variant/20 flex items-center justify-center overflow-hidden shrink-0">
                  <img 
                    src={user?.avatarUrl || `https://ui-avatars.com/api/?name=${user?.name || "User"}&background=067a52&color=fff`} 
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-black text-on-surface tracking-tight truncate max-w-[180px]">{user?.name}</h3>
                  <p className="text-[10px] font-bold text-primary uppercase tracking-widest">{role.replace("_", " ")}</p>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest">Email Address</p>
                <p className="text-sm font-bold text-on-surface truncate">{user?.email}</p>
              </div>
              <div className="space-y-1 mt-2">
                <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest">Membership Plan</p>
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 mt-1 rounded-lg border ${tierDetails.color} font-bold text-xs uppercase tracking-widest w-max`}>
                  <span className="material-symbols-outlined text-[14px]">{tierDetails.icon}</span>
                  {membership}
                </div>
              </div>
            </div>
              <button 
                onClick={handleSSOHandoff}
                disabled={isSubmitting}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? "Connecting to Hub..." : "Update Password"}
                <span className="material-symbols-outlined text-sm">key</span>
              </button>
          </div>

          {/* Manage Topics Card (Manager Exclusive) */}
          {isManager && (
            <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm hover:shadow-md transition-all group relative overflow-hidden flex flex-col justify-between">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 group-hover:scale-110 transition-transform" />
              
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-2xl">label</span>
                </div>
                <div>
                  <h4 className="text-xl font-black text-on-surface tracking-tight">Curriculum Topics</h4>
                  <p className="text-xs font-medium text-on-surface-variant">Classify and organize course content.</p>
                </div>
              </div>

              <div className="py-6 flex flex-col items-center">
                <TopicCounter />
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-on-surface-variant mt-1">Total Topics</p>
              </div>

              <button 
                onClick={() => setShowManager("topics")}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                Manage Topics
                <span className="material-symbols-outlined text-sm">settings_suggest</span>
              </button>
            </div>
          )}

          {/* Manage Categories Card (Manager Exclusive) */}
          {isManager && (
            <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm hover:shadow-md transition-all group relative overflow-hidden flex flex-col justify-between">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 group-hover:scale-110 transition-transform" />
              
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-2xl">folder</span>
                </div>
                <div>
                  <h4 className="text-xl font-black text-on-surface tracking-tight">Course Categories</h4>
                  <p className="text-xs font-medium text-on-surface-variant">Group courses under core subject pillars.</p>
                </div>
              </div>

              <div className="py-6 flex flex-col items-center">
                <CategoryCounter />
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-on-surface-variant mt-1">Total Categories</p>
              </div>

              <button 
                onClick={() => setShowManager("categories")}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                Manage Categories
                <span className="material-symbols-outlined text-sm">folder_open</span>
              </button>
            </div>
          )}

          {/* Manage Membership Limits Card (ADMIN ONLY) */}
          {isAdmin && (
            <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm hover:shadow-md transition-all group relative overflow-hidden flex flex-col justify-between">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 group-hover:scale-110 transition-transform" />
              
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-2xl">key</span>
                </div>
                <div>
                  <h4 className="text-xl font-black text-on-surface tracking-tight">Membership Limits</h4>
                  <p className="text-xs font-medium text-on-surface-variant">Control strategy + poster combined creation limits.</p>
                </div>
              </div>

              <div className="py-6 flex flex-col items-center text-center">
                <span className="material-symbols-outlined text-4xl text-primary">lock_open</span>
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-on-surface-variant mt-2">Combined Creation Quotas</p>
              </div>

              <button 
                onClick={() => setShowManager("limits")}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                Manage Limits
                <span className="material-symbols-outlined text-sm">tune</span>
              </button>
            </div>
          )}

          {/* System Utilities Card (ADMIN ONLY) */}
          {isAdmin && (
            <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm hover:shadow-md transition-all group relative overflow-hidden flex flex-col justify-between">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 group-hover:scale-110 transition-transform" />
              
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-2xl">build_circle</span>
                </div>
                <div>
                  <h4 className="text-xl font-black text-on-surface tracking-tight">System Utilities</h4>
                  <p className="text-xs font-medium text-on-surface-variant">Server management and background tasks.</p>
                </div>
              </div>

              <div className="py-6 flex flex-col items-center text-center">
                <span className="material-symbols-outlined text-4xl text-primary">settings_applications</span>
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-on-surface-variant mt-2">Zero-SSH Operations</p>
              </div>

              <button 
                onClick={() => navigate("/admin/utilities")}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                Open Utilities
                <span className="material-symbols-outlined text-sm">terminal</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="animate-in fade-in zoom-in-95 duration-500 px-2 space-y-6">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setShowManager(null)}
              className="flex items-center gap-2 text-primary font-black text-[10px] uppercase tracking-widest hover:gap-3 transition-all cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">arrow_back</span>
              Back to Settings
            </button>
          </div>
          
          <div className="bg-surface-container-low border border-outline-variant/10 p-6 md:p-10 rounded-[2.5rem] shadow-sm">
            {showManager === "topics" ? (
              <TopicManager />
            ) : showManager === "categories" ? (
              <CategoryManager />
            ) : (
              <MembershipLimitManager />
            )}
          </div>
        </div>
      )}

    </div>
  );
}

